import {
  Text,
  Button,
  InputWrapper,
  Input,
  Space,
  Drawer,
  createStyles,
  Group,
  Switch,
  NumberInput,
  Select,
  SimpleGrid,
  Tabs,
  Divider,
  Alert,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import { DatePicker, DateRangePicker } from "@mantine/dates";
import { AlertTriangle } from "lucide-react";
import { RRule } from "rrule";

import {
  IBlockedDateEmployees,
  OdpBlockedDateContextType,
} from "../../@types/blockedDates";
import { OdpBlockedDatesContext } from "../../providers/BlockedDatesProvider";

import { ConfirmCreateBlockedDates } from "./ConfirmCreateBlockedDates";
import { BlockedDatesAppliedSuccessfully } from "./BlockedDatesAppliedSuccessfully";
import EmployeesBlocked from "./EmployeesBlocked";

const useStyles = createStyles((theme) => ({
  blockedDatesCTA: {
    position: "absolute",
    bottom: "0",
    left: "0",
    padding: "0 10px 10px 10px",
    boxSizing: "border-box",
    width: "100%",

    [`@media (max-width: ${theme.breakpoints.xs}px)`]: {
      padding: "0",
      position: "initial",
    },
  },
  byWeekDay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
    height: 42,
    width: 42,
    cursor: "pointer",
    background: "rgba(6, 33, 51, 0.10)",
    border: "none",
  },
}));

type CreateNewBlockedDatesProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

const CreateNewBlockedDates: React.FC<CreateNewBlockedDatesProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const {
    setRecurrenceEndDate,
    byMonthDayStart,
    setByMonthDayStart,
    setByMonthDayEnd,
    frequencyValue,
    setRepeatInterval,
    frequencyOptions,
    isUpdatedSuccessfully,
    setIsUpdatedSuccessfully,
    toggleByWeekDay,
    setNewTitle,
    blockReasons,
    setBlockReason,
    setNewDateRange,
    updateOrCreateNewBlockDates,
    updateOrCreateEmployeesBlockedDates,
    isValidData,
    setFrequencyValue,
    byMonthDayEnd,
    isUpdatingLoading,
    repeatInterval,
    yearCrossingError,
  } = React.useContext(OdpBlockedDatesContext) as OdpBlockedDateContextType;

  const [isBlockDateRepeating, setIsBlockDateRepeating] =
    useState<boolean>(false);
  const startDayOptions = Array.from(
    { length: 31 },
    (_, index) => index + 1
  ).map((n) => ({
    label: n.toString(),
    value: n.toString(),
  }));

  const [endDayOptions, setEndDayOptions] = useState(
    Array.from({ length: 31 }, (_, index) => index + 1).map((n) => ({
      label: n.toString(),
      value: n.toString(),
    }))
  );

  const [isConfirmingCreate, setIsConfirmingCreate] = useState(false);

  const [currentDateRange, setCurrentDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);

  const [selectedWeekDays, setSelectedWeekDays] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<number>(0);

  const [employeesCreate, setEmployeesCreate] = useState<
    IBlockedDateEmployees[]
  >([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [employeesDelete, setEmployeesDelete] = useState<
    IBlockedDateEmployees[]
  >([]);
  const [isEmployeeBlockedDate, setIsEmployeeBlockedDate] =
    useState<boolean>(false);

  const weekdays = [
    {
      label: "Sun",
      rule: RRule.SU.toString(),
    },
    {
      label: "Mon",
      rule: RRule.MO.toString(),
    },
    {
      label: "Tue",
      rule: RRule.TU.toString(),
    },
    {
      label: "Wed",
      rule: RRule.WE.toString(),
    },
    {
      label: "Thu",
      rule: RRule.TH.toString(),
    },
    {
      label: "Fri",
      rule: RRule.FR.toString(),
    },
    {
      label: "Sat",
      rule: RRule.SA.toString(),
    },
  ];
  const { classes } = useStyles();

  const getFrequencyValue = (): string => {
    if (frequencyValue === RRule.MONTHLY) {
      return RRule.MONTHLY.toString();
    }
    if (frequencyValue === RRule.WEEKLY) {
      return RRule.WEEKLY.toString();
    }
    return RRule.YEARLY.toString();
  };
  const updateDateRange = (): void => {
    if (!currentDateRange[0] && !currentDateRange[1]) {
      // if the date in the view is cleared update the state
      setNewDateRange(currentDateRange);
    }
  };

  const saveBlockedDates = (): void => {
    setIsConfirmingCreate(false);
    if (isEmployeeBlockedDate) {
      updateOrCreateEmployeesBlockedDates({
        employeesCreate,
        employeesDelete: [],
      });
    } else {
      updateOrCreateNewBlockDates();
    }
  };

  const setDefaultValue = (checked: boolean): void => {
    // reset
    setByMonthDayStart(0);
    setByMonthDayEnd(0);

    setCurrentDateRange([null, null]);
    setNewDateRange([null, null]);

    if (checked) {
      setFrequencyValue(RRule.YEARLY);
    } else {
      setFrequencyValue(RRule.DAILY);
    }
  };
  useEffect(() => {
    const updatedOptions = Array.from({ length: 31 }, (_, index) => index + 1)
      .map((n) => ({
        label: n.toString(),
        value: n.toString(),
      }))
      .filter((d) => Number(d.value) >= byMonthDayStart);

    // reset
    if (byMonthDayStart > byMonthDayEnd) {
      setByMonthDayEnd(0);
    }
    setEndDayOptions(updatedOptions);
  }, [byMonthDayStart]);

  const isEmployeeCreateValid = (): boolean => {
    if (!isEmployeeBlockedDate) {
      return true;
    }
    if (employeesCreate.length === 0) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    setIsBlockDateRepeating(false);
    setDefaultValue(false);
    setRepeatInterval(1);
  }, [isOpen]);

  return (
    <Drawer
      title={
        <Group>
          <Text
            size="xl"
            style={{
              fontSize: "18px",
              fontWeight: 700,
              lineHeight: "24.59px",
            }}
          >
            Block Transaction Dates
          </Text>
        </Group>
      }
      opened={isOpen}
      size="xl"
      onClose={() => {
        setIsOpen(false);
      }}
      styles={{
        header: {
          backgroundColor: "#ffffff",
          borderTopLeftRadius: "15px",
          borderBottomLeftRadius: "15px",
        },
        drawer: {
          backgroundColor: "#ffffff",
          borderTopLeftRadius: "15px",
          borderBottomLeftRadius: "15px",
        },
      }}
      overlayOpacity={0.1}
      overlayBlur={3}
      position="right"
    >
      <ConfirmCreateBlockedDates
        isOpen={isConfirmingCreate}
        setOpen={setIsConfirmingCreate}
        updateBlockedDate={saveBlockedDates}
      />

      <BlockedDatesAppliedSuccessfully
        isOpen={isUpdatedSuccessfully}
        setIsOpen={setIsUpdatedSuccessfully}
        setIsCreateNew={setIsOpen}
        lang={{
          title: "Block Dates Applied Successfully",
          subtitle: "Your block dates are active.",
          description:
            "Employees won't be able to take advances during the set dates.",
        }}
      />

      <Tabs
        px={41}
        active={activeTab}
        onTabChange={(value) => {
          setActiveTab(value);
        }}
        styles={{
          tabLabel: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            fontWeight: "bold !important",
          },
          tabControl: {
            padding: 0,
          },
        }}
      >
        <Tabs.Tab label="Set Dates">
          <div style={{ overflow: "auto", marginTop: "32px" }}>
            <Text
              style={{
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "20px",
                marginBottom: "8px",
              }}
            >
              <span>Title</span>
              <span style={{ color: "#ED7687" }}> *</span>
            </Text>
            <InputWrapper id="input-title" required>
              <Input
                id="input-title"
                placeholder="Cash Flow Management"
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </InputWrapper>

            {/* isBlockDateRepeating? */}
            <span
              style={{
                lineHeight: "16px",
                marginTop: "32px",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                Recurring Dates Setup
              </Text>
            </span>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "20px",
                }}
              >
                Repeat block dates?
              </Text>
              <Switch
                checked={isBlockDateRepeating}
                onChange={(s) => {
                  setIsBlockDateRepeating(s.target.checked);
                  setDefaultValue(s.target.checked);
                }}
                size="sm"
              />
            </div>

            {/* Block Reason */}
            {blockReasons.length > 0 && (
              <div style={{ marginTop: "32px" }}>
                <Text
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "20px",
                    marginBottom: "8px",
                  }}
                >
                  <span>Reason for Block?</span>
                  <span style={{ color: "#ED7687" }}> *</span>
                </Text>
                <Select
                  id="block-reason"
                  data={blockReasons
                    .filter((order) => order.attributes.Active)
                    .sort((a, b) => a.attributes.Order - b.attributes.Order)
                    .map((r) => r.attributes.Reason)}
                  required
                  onChange={(val) => setBlockReason(val ?? "")}
                />
              </div>
            )}

            {/* Once OFF Dates */}
            {!isBlockDateRepeating && frequencyValue === RRule.DAILY && (
              <>
                <Text
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "20px",
                    marginBottom: "8px",
                    marginTop: "32px",
                  }}
                >
                  <span>Select Blocked Dates</span>
                  <span style={{ color: "#ED7687" }}> *</span>
                </Text>
                <InputWrapper>
                  <DateRangePicker
                    allowLevelChange={false}
                    allowSingleDateInRange
                    placeholder="DD/MM/YYYY"
                    value={currentDateRange}
                    onChange={(range) => {
                      setNewDateRange(range);
                      setCurrentDateRange(range);
                    }}
                    onDropdownClose={() => {
                      updateDateRange();
                    }}
                  />
                </InputWrapper>
              </>
            )}

            {/* OCCURANCE */}
            {isBlockDateRepeating && (
              <div style={{ paddingLeft: "12px" }}>
                <Space h="xl" />

                <Group>
                  <Text
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      lineHeight: "20px",
                    }}
                  >
                    <span>Repeat Every</span>
                    <span style={{ color: "#ED7687" }}> *</span>
                  </Text>

                  <NumberInput
                    hideControls
                    style={{ width: 42 }}
                    defaultValue={repeatInterval}
                    onChange={(i: number) => setRepeatInterval(i)}
                  />

                  <Select
                    defaultValue={getFrequencyValue()}
                    onChange={(opt: string) => setFrequencyValue(Number(opt))}
                    data={frequencyOptions}
                  />

                  {repeatInterval > 1 && frequencyValue === RRule.YEARLY && (
                    <Text
                      style={{
                        fontSize: "12px",
                        color: "rgba(6, 33, 51, 0.40)",
                        marginTop: "-8px",
                      }}
                    >
                      By selecting a repeat {repeatInterval} times a year, it
                      will occur every {repeatInterval} years. For example, if
                      you choose to repeat 2 times a year, the event will take
                      place every 2nd year. For more frequent repeats within a
                      year, consider choosing monthly to meet your desired
                      needs.
                    </Text>
                  )}

                  {repeatInterval > 1 && frequencyValue === RRule.MONTHLY && (
                    <Text
                      style={{
                        fontSize: "12px",
                        color: "rgba(6, 33, 51, 0.40)",
                        marginTop: "-8px",
                      }}
                    >
                      By selecting a repeat {repeatInterval} times a month, it
                      will occur every {repeatInterval} months. For example, if
                      you choose to repeat 2 times a month, the event will take
                      place every 2nd month. For more frequent repeats within a
                      year, consider choosing weekly to meet your desired needs.
                    </Text>
                  )}
                </Group>
              </div>
            )}

            {/* YEARLY */}
            {isBlockDateRepeating && frequencyValue === RRule.YEARLY && (
              <div style={{ paddingLeft: "12px" }}>
                <Space h="xl" />

                <Text
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: "20px",
                    marginBottom: "8px",
                  }}
                >
                  <span>Select Blocked Dates</span>
                  <span style={{ color: "#ED7687" }}> *</span>
                </Text>

                <InputWrapper required>
                  <DateRangePicker
                    labelFormat="MMMM"
                    inputFormat="MMMM DD"
                    allowSingleDateInRange
                    placeholder="DD/MM/YYYY"
                    value={currentDateRange}
                    onChange={(range) => {
                      setNewDateRange(range);
                    }}
                  />
                </InputWrapper>

                {/* SW-7558: Year-crossing validation error display */}
                {yearCrossingError && (
                  <Alert
                    icon={<AlertTriangle size={16} />}
                    title="Invalid Date Range"
                    color="red"
                    mt="md"
                    styles={{
                      root: { backgroundColor: "#FFF5F5" },
                      title: { color: "#C92A2A", fontWeight: 600 },
                      message: { color: "#862E2E" },
                    }}
                  >
                    {yearCrossingError}
                    <Text size="sm" mt="xs" style={{ color: "#862E2E" }}>
                      Turn off{" "}
                      <Text component="span" weight={700}>
                        Repeat block dates
                      </Text>{" "}
                      if you want to block a longer time span.
                    </Text>
                  </Alert>
                )}
              </div>
            )}

            {/* MONTHLY */}
            {isBlockDateRepeating && frequencyValue === RRule.MONTHLY && (
              <div style={{ paddingLeft: "12px" }}>
                <Space h="xl" />
                <Group>
                  <Text style={{ fontSize: "12px", fontWeight: 500 }}>
                    <span>Start</span>
                    <span style={{ color: "#ED7687" }}> *</span>
                  </Text>
                  <div style={{ width: 64 }}>
                    <Select
                      onChange={(opt) => setByMonthDayStart(Number(opt))}
                      // defaultValue={}
                      data={startDayOptions}
                    />
                  </div>

                  <Text style={{ fontSize: "12px", fontWeight: 500 }}>
                    End <span style={{ color: "#ED7687" }}> *</span>
                  </Text>
                  <div style={{ width: 64 }}>
                    <Select
                      disabled={byMonthDayStart < 1}
                      onChange={(opt) => {
                        setByMonthDayEnd(Number(opt));
                      }}
                      value={byMonthDayEnd.toString()}
                      data={endDayOptions}
                    />
                  </div>
                </Group>
              </div>
            )}

            {/* WEEKLY */}
            {isBlockDateRepeating && frequencyValue === RRule.WEEKLY && (
              <div style={{ paddingLeft: "12px" }}>
                <Space h="xl" />
                <Text
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: "20px",
                    marginBottom: "8px",
                  }}
                >
                  <span>Repeat On</span>
                  <span style={{ color: "#ED7687" }}> *</span>
                </Text>

                <Group>
                  <SimpleGrid cols={7}>
                    {weekdays.map((day) => (
                      <button
                        key={day.label}
                        className={classes.byWeekDay}
                        onClick={() => {
                          toggleByWeekDay(day.rule);
                          if (selectedWeekDays.includes(day.label)) {
                            setSelectedWeekDays((prev) =>
                              prev.filter((temp) => temp !== day.label)
                            );
                          } else {
                            setSelectedWeekDays((prev) => [...prev, day.label]);
                          }
                        }}
                        style={
                          selectedWeekDays.includes(day.label)
                            ? {
                                backgroundColor: "#ED7687",
                                color: "white",
                              }
                            : {}
                        }
                      >
                        {day.label}
                      </button>
                    ))}
                  </SimpleGrid>
                </Group>
              </div>
            )}

            {/* END DATE */}
            {isBlockDateRepeating && (
              <div style={{ paddingLeft: "12px" }}>
                <Space h="xl" />
                <Text
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    marginBottom: "8px",
                  }}
                >
                  <span>Set End Date for Recurring Blocked Dates</span>
                  <span style={{ color: "#ED7687" }}> *</span>
                </Text>

                <Text
                  style={{
                    fontSize: "12px",
                    color: "rgba(6, 33, 51, 0.40)",
                  }}
                >
                  Specify the date when a recurring EWA block comes to an end.
                  By setting the end date, you ensure it no longer extends
                  beyond the specified end date
                </Text>

                <DatePicker
                  placeholder="Specify End Date"
                  required
                  onChange={(endDate: Date) => setRecurrenceEndDate(endDate)}
                  style={{ marginTop: "8px" }}
                />
              </div>
            )}

            <Space h="xl" />
            <span
              style={{
                lineHeight: "16px",
                marginBottom: "8px",
                marginTop: "10px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                Employee-Specific Restrictions
              </Text>
            </span>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: "20px",
                  }}
                >
                  Restrict Access for Specific Employees?
                </Text>
                <Switch
                  size="sm"
                  checked={isEmployeeBlockedDate}
                  onChange={(s) => setIsEmployeeBlockedDate(s.target.checked)}
                />
              </div>
              <Space h="xl" />

              {isOpen && employeesCreate && (
                <>
                  <EmployeesBlocked
                    isEmployeeBlockedDate={isEmployeeBlockedDate}
                    employeesCreate={employeesCreate}
                    employeesDelete={[]}
                    setEmployeesCreate={setEmployeesCreate}
                    setEmployeesDelete={setEmployeesDelete}
                  />
                  <Space h="xl" />
                </>
              )}
            </div>
            <Divider
              mt={50}
              style={{ justifySelf: "flex-end", alignSelf: "flex-end" }}
            />
            <Space h="xl" />

            {/* Drawer Actions */}
            <div
              style={
                isBlockDateRepeating
                  ? {
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "space-between",
                      marginTop: "100px",
                      marginBottom: "50px",
                      width: "100%",
                    }
                  : {
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "space-evenly",
                      bottom: "50px",
                      marginBottom: "50px",
                      left: "0",
                      width: "100%",
                    }
              }
            >
              <Button
                variant="outline"
                style={{
                  height: "40px",
                  padding: "0 20px",
                  width: "200px",
                  marginBottom: "50px",
                }}
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                loading={isUpdatingLoading}
                disabled={!(isValidData() && isEmployeeCreateValid())}
                onClick={() => setIsConfirmingCreate(true)}
                style={{
                  height: "40px",
                  padding: "0 20px",
                  width: "200px",
                }}
              >
                Save Date
              </Button>
            </div>
          </div>
        </Tabs.Tab>
      </Tabs>
    </Drawer>
  );
};

export default CreateNewBlockedDates;
